import { cartState } from "./cart-state";

/**
 * The `/cart` page.
 *
 * Registers its own section so a quantity change re-renders line totals,
 * discounts and the order subtotal in the same request that writes the cart.
 * Previously the page only reloaded on removal (`location.reload()`) and left
 * every total stale on any other change.
 */
class CartPage extends HTMLElement {
  connectedCallback(): void {
    const sectionId = this.dataset.sectionId;
    const templateUrl = this.dataset.templateUrl;
    if (!sectionId) return;

    cartState.registerSection(sectionId, templateUrl);
    document.addEventListener("cart:section", this.onSection);
    this.addEventListener("submit", this.onSubmit);
  }

  disconnectedCallback(): void {
    document.removeEventListener("cart:section", this.onSection);
  }

  private onSection = (event: Event): void => {
    const { id, html } = (event as CustomEvent<{ id: string; html: string }>)
      .detail;
    if (id !== this.dataset.sectionId) return;

    const parsed = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector("cart-page");
    if (!parsed) return;

    this.innerHTML = parsed.innerHTML;

    // Fresh markup is internally consistent — if the cart is empty this is now
    // the server's own empty branch — so the client-painted one must come off.
    this.classList.remove("is-empty");
  };

  /**
   * Checkout is where quantity edits are paid for. Everything since the last
   * page load goes out in one write, and only then does the form submit —
   * otherwise checkout would start from the quantities the server still holds.
   */
  private onSubmit = (event: SubmitEvent): void => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    const submitter = event.submitter;
    const submitterName = submitter?.getAttribute("name");
    const submitterValue =
      submitter instanceof HTMLButtonElement ||
      submitter instanceof HTMLInputElement
        ? submitter.value
        : "";

    cartState.verifyBeforeCheckout().then((unchanged) => {
      if (!unchanged) return;

      // A pending write can re-render this section while we waited, which
      // replaces the form node — resubmit whatever is live now, not the stale
      // `form` captured above.
      const liveForm = this.querySelector<HTMLFormElement>("#cartform");
      if (!liveForm) return;

      // `form.submit()` drops the clicked button's name/value pair (`checkout`
      // vs. `update`), so Shopify can't tell them apart and sends the
      // customer back to the cart page either way. A hidden field stands in
      // for it — simpler and more reliable than `requestSubmit(submitter)`,
      // which throws if the submitter isn't recognised as owned by this exact
      // form node.
      if (submitterName) {
        let field = liveForm.querySelector<HTMLInputElement>(
          `input[type="hidden"][name="${CSS.escape(submitterName)}"]`,
        );
        if (!field) {
          field = document.createElement("input");
          field.type = "hidden";
          field.name = submitterName;
          liveForm.appendChild(field);
        }
        field.value = submitterValue;
      }

      liveForm.submit();
    });
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "cart-page": CartPage;
  }
}

if (!window.customElements.get("cart-page")) {
  window.customElements.define("cart-page", CartPage);
}

export {};
